// frontend/src/widgets/CustomerFormWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-09
// Updated:      2026-09-23 (global mobile modal host contract)
// Stage 1:    2026-10-26 14:16 GMT — Add independent Is Vendor field; preserve CRM lifecycle.
//
// Description:
//   Customer create/update form with lookup selects + contact modal flow.
//   ✅ Aligns account_manager_id & closest_resource_id to resources.id (DB FK)
//   ✅ Payload guard: invalid resource IDs are nulled pre-POST/PUT
//   ✅ Sanitizes payload: drops "" & unset dates; never sends internal_reference
//   ✅ “Add Contact” on create: autosaves customer then triggers parent callback
//   ✅ Robust API base resolver (no TS parse errors)
//   ✅ Full diagnostics on request failure (no fake errors)
//
// Mobile Responsiveness (2026-09-08):
//   • Preserves the existing desktop two-column customer form at >= 768px.
//   • Reflows all nested form groups into a single-column mobile flow.
//   • Makes text fields, selects, dates, and actions touch-safe on mobile.
//   • Keeps all create/update, lookup, FK validation, payload sanitisation,
//     and Save & Add Contact behaviour unchanged.
//   • Expands the nearest ReactGridLayout host to the form's natural mobile
//     height because mobile widgets do not expose the desktop resize handle.
//   • Uses one mounted form and one set of lookup requests; no duplicate mobile UI.
//   • Adds an optional onCustomerSaved callback so non-widget hosts can close
//     after a successful create without changing existing widget behaviour.
//   • Allows non-widget hosts to hide Save & Add Contact until that workflow is
//     available in the host surface.
//
//   Why you saw the regression:
//   The backend constraint is customers.account_manager_id → resources(id).
//   If the UI sends a *user* id or a stale id (e.g. 11) that isn’t in `resources`,
//   Postgres throws ForeignKeyViolation. This file exclusively uses resources, and
//   nulls any invalid ids before sending, so the insert won’t violate that FK.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/* =========================================================
 * API base (no trailing slash)
 * - Frontend :8010 or :8070 → backend :8020/api
 * - Backend  :8020         → same origin /api
 * - VITE_API_BASE wins if set
 * =======================================================*/
const API_BASE = (() => {
  let envBase;
  try {
    // Safe in Vite; wrapped to avoid TS "typeof import" snafu
    envBase = import.meta?.env?.VITE_API_BASE;
  } catch {
    envBase = undefined;
  }
  if (envBase) return String(envBase).replace(/\/+$/, "");

  const { protocol, hostname, port, origin } = window.location;

  if (port === "8010" || port === "8070") return `${protocol}//${hostname}:8020/api`;
  if (port === "8020") return `${protocol}//${hostname}:8020/api`;

  // Fallback if you reverse proxy /api
  return `${origin}/api`;
})();

const API = {
  users: `${API_BASE}/users`,        // still used elsewhere in app; not here
  resources: `${API_BASE}/resources`,
  customers: `${API_BASE}/customers`,
  contacts: `${API_BASE}/contacts`,
};

const CATEGORY_OPTIONS = [
  "Limited",
  "Charity",
  "Transport Authority",
  "Partnership",
  "Sole Trader",
  "LLP",
  "Public Sector",
];

const defaultCustomer = {
  // Core
  name: "",
  nickname: "",
  website: "",
  email: "",
  phone_number: "",
  status: "Active",
  is_vendor: false,
  notes: "",

  // Trading address
  address_line_1: "",
  address_line_2: "",
  address_line_3: "",
  city: "",
  county: "",
  postcode: "",
  country: "United Kingdom",

  // Registered office
  reg_address_line_1: "",
  reg_address_line_2: "",
  reg_address_line_3: "",
  reg_city: "",
  reg_county: "",
  reg_postcode: "",

  // Company info
  company_number: "",
  sic_code: "",
  nature_of_business: "",
  formerly_known_as: "",
  customer_since: "", // yyyy-mm-dd or ""

  category: "",

  // Associations (these must be resource ids where applicable)
  account_manager_id: null,
  closest_resource_id: null,
  parent_customer_id: null,
  primary_contact_id: null,
  billing_contact_id: null,

  // Read-only DB generated (never send)
  internal_reference: "",
};

/* ===========================================
 * Tiny UI primitives
 * =========================================*/
const Field = React.memo(function Field({
  as = "input",
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  disabled = false,
  className = "",
  rows,
  required = false,
}) {
  const base =
    "px-3 py-2.5 min-h-[44px] md:min-h-0 border border-gray-300 rounded-md text-base md:text-sm text-gray-800 w-full bg-white disabled:opacity-60 " +
    className;

  const commonProps = { name, value: value ?? "", onChange, placeholder, disabled, required };

  if (as === "textarea") {
    return (
      <div className="flex flex-col gap-1">
        {label && <label className="text-xs text-gray-600">{label}</label>}
        <textarea {...commonProps} rows={rows || 3} className={base + " resize-none"} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs text-gray-600">{label}</label>}
      <input {...commonProps} type={type} className={base} />
    </div>
  );
});

const Select = React.memo(function Select({
  label,
  name,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  className = "",
  required = false,
}) {
  return (
    <div className="flex flex-col gap-1">
      {label && <label className="text-xs text-gray-600">{label}</label>}
      <select
        name={name}
        value={value ?? ""}
        onChange={onChange}
        disabled={disabled}
        required={required}
        className={
          "px-3 py-2.5 min-h-[44px] md:min-h-0 border border-gray-300 rounded-md text-base md:text-sm text-gray-800 bg-white disabled:opacity-60 w-full " +
          className
        }
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label ?? `#${o.id}`}
          </option>
        ))}
      </select>
    </div>
  );
});

/* ===========================================
 * Main component
 * =========================================*/
const CustomerFormWidget = ({
  customer = null,
  onClose = null,
  onCustomerCreated = null, // (newCustomerId:number) => void
  onCustomerSaved = null, // (createdCustomer:object) => void
  showAddContactAction = true,
}) => {
  const isEdit = Boolean(customer && customer.id);
  const [form, setForm] = useState(isEdit ? { ...defaultCustomer, ...customer } : defaultCustomer);

  // lookups
  const [resources, setResources] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [contacts, setContacts] = useState([]);

  // ui state
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const mountedRef = useRef(true);
  const rootRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    const updateViewportMode = () => {
      setIsMobile(window.innerWidth < 768);
    };

    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);

    return () => {
      window.removeEventListener("resize", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const gridHost = root.closest(".react-grid-item");
    if (!gridHost) return undefined;

    const previousHeight = gridHost.style.height;
    const previousMinHeight = gridHost.style.minHeight;

    let frameId = null;
    let observer = null;

    const restoreDesktopHeight = () => {
      gridHost.style.height = previousHeight;
      gridHost.style.minHeight = previousMinHeight;
    };

    const syncMobileHostHeight = () => {
      if (frameId) cancelAnimationFrame(frameId);

      frameId = requestAnimationFrame(() => {
        if (!isMobile) {
          restoreDesktopHeight();
          return;
        }

        const hostRect = gridHost.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        const topOffset = Math.max(0, rootRect.top - hostRect.top);
        const requiredHeight = Math.ceil(topOffset + root.scrollHeight + 8);

        gridHost.style.height = `${requiredHeight}px`;
        gridHost.style.minHeight = `${requiredHeight}px`;
      });
    };

    syncMobileHostHeight();
    window.addEventListener("resize", syncMobileHostHeight);

    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(syncMobileHostHeight);
      observer.observe(root);
    }

    return () => {
      window.removeEventListener("resize", syncMobileHostHeight);
      if (observer) observer.disconnect();
      if (frameId) cancelAnimationFrame(frameId);
      restoreDesktopHeight();
    };
  }, [isMobile, submitting, message, errors]);

  const token = localStorage.getItem("anpr_token");
  const authConfig = useMemo(
    () => ({
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  /* ===========================================
   * Load lookups (resources + customers)
   * =========================================*/
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rRes, cRes] = await Promise.all([
          axios.get(`${API.resources}?limit=2000`, authConfig),
          axios.get(`${API.customers}?limit=1000`, authConfig),
        ]);
        if (cancelled || !mountedRef.current) return;

        const rData = Array.isArray(rRes.data) ? rRes.data : [];
        const cData = Array.isArray(cRes.data) ? cRes.data : [];

        setResources(rData);
        setAllCustomers(cData);
      } catch (e) {
        if (cancelled || !mountedRef.current) return;
        console.warn("Lookup load failed (resources/customers):", e?.response?.data || e?.message || e);
        setResources([]);
        setAllCustomers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authConfig]);

  // Load contacts for edit state (used in Primary/Billing)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isEdit) {
        setContacts([]);
        return;
      }
      try {
        const res = await axios.get(`${API.contacts}?limit=1000`, authConfig);
        if (cancelled || !mountedRef.current) return;
        const all = Array.isArray(res.data) ? res.data : [];
        const mine = customer?.id ? all.filter((c) => Number(c.customer_id) === Number(customer.id)) : [];
        setContacts(mine);
      } catch (e) {
        if (cancelled || !mountedRef.current) return;
        console.warn("Contacts load failed:", e?.response?.data || e?.message || e);
        setContacts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, customer?.id, authConfig]);

  // Keep form synced if parent swaps the record under us
  useEffect(() => {
    if (isEdit) {
      setForm({ ...defaultCustomer, ...customer });
    } else {
      setForm(defaultCustomer);
    }
  }, [isEdit, customer]);

  /* ===========================================
   * Helpers
   * =========================================*/
  const toIntOrNull = (v) => {
    if (v === "" || v === null || typeof v === "undefined") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    const numericFKs = new Set([
      "account_manager_id",
      "closest_resource_id",
      "parent_customer_id",
      "primary_contact_id",
      "billing_contact_id",
    ]);

    if (numericFKs.has(name)) {
      setForm((prev) => ({ ...prev, [name]: toIntOrNull(value) }));
      return;
    }

    if (type === "date") {
      setForm((prev) => ({ ...prev, [name]: value || "" }));
      return;
    }

    if (name === "internal_reference") return; // read-only defense

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // optional validation (client side)
  const validate = (payload) => {
    const errs = {};
    if (!payload.name || String(payload.name).trim().length === 0) {
      errs.name = "Company Name is required";
    }
    // FK checks
    const resourceIdSet = new Set(resources.map((r) => Number(r.id)));
    if (payload.account_manager_id && !resourceIdSet.has(Number(payload.account_manager_id))) {
      errs.account_manager_id = "Selected Account Manager is not a valid resource";
    }
    if (payload.closest_resource_id && !resourceIdSet.has(Number(payload.closest_resource_id))) {
      errs.closest_resource_id = "Selected Closest Resource is not a valid resource";
    }
    return errs;
  };

  // Build API payload
  const buildPayload = () => {
    const payload = { ...form };

    // drop empty strings
    Object.keys(payload).forEach((k) => {
      const v = payload[k];
      if (typeof v === "string" && v.trim() === "") delete payload[k];
    });

    // date unset
    if (payload.customer_since === "") delete payload.customer_since;

    // never send internal reference
    delete payload.internal_reference;

    // avoid self-parent on edit
    if (isEdit && customer?.id && payload.parent_customer_id === customer.id) {
      payload.parent_customer_id = null;
    }

    // Final FK guard (prevents the exact FK error you hit)
    const resourceIdSet = new Set(resources.map((r) => Number(r.id)));
    if (payload.account_manager_id && !resourceIdSet.has(Number(payload.account_manager_id))) {
      console.warn("⚠️ account_manager_id not a valid resource; nulling");
      payload.account_manager_id = null;
    }
    if (payload.closest_resource_id && !resourceIdSet.has(Number(payload.closest_resource_id))) {
      console.warn("⚠️ closest_resource_id not a valid resource; nulling");
      payload.closest_resource_id = null;
    }

    return payload;
  };

  const logAxiosFailure = (err) => {
    const info = {
      url: err?.config?.url,
      method: err?.config?.method,
      status: err?.response?.status,
      code: err?.code,
      detail:
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data : undefined) ||
        err?.message,
    };
    console.error("❌ Request failed", info);
  };

  const handleSubmit = async (e, openContactAfterCreate = false) => {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    setMessage(null);
    setErrors({});

    try {
      const payload = buildPayload();
      const errs = validate(payload);
      if (Object.keys(errs).length) {
        setErrors(errs);
        console.warn("❕ Client validation failed:", errs);
        setSubmitting(false);
        return;
      }

      if (isEdit) {
        const url = `${API.customers}/${customer.id}`;
        console.debug("➡️ PUT", url, payload);
        await axios.put(url, payload, authConfig);
        console.info("✅ Updated customer:", customer.id);
        setMessage("Customer updated successfully");
        onClose && onClose();
      } else {
        // One clear request (no fake errors). Backend registers both slash/no-slash.
        const url = `${API.customers}`;
        console.debug("➡️ POST", url, payload);
        const res = await axios.post(url, payload, authConfig);
        const created = res?.data?.customer || res?.data;
        console.info("✅ Created customer:", created?.id);
        setMessage("Customer created successfully");
        setForm(defaultCustomer);
        if (onCustomerSaved && created?.id) {
          onCustomerSaved(created);
        }
        if (openContactAfterCreate && onCustomerCreated && created?.id) {
          onCustomerCreated(created.id);
        }
      }
    } catch (err) {
      logAxiosFailure(err);

      // Helpful hint for your exact FK case
      const fkMsg =
        String(err?.response?.data || "").includes("ForeignKeyViolation") ||
        String(err?.response?.data?.detail || "").match(/violates foreign key constraint.*resources/);

      if (fkMsg) {
        setMessage(
          "Failed to save customer (invalid Account Manager / Closest Resource). Choose a valid resource or clear the field."
        );
      } else {
        setMessage("Failed to save customer");
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  /* ===========================================
   * Options
   * =========================================*/
  const resourceOptions = useMemo(() => {
    return resources.map((r) => {
      const name =
        [r.first_name, r.last_name].filter(Boolean).join(" ").trim() ||
        r.display_name ||
        r.username ||
        r.email;
      return { id: r.id, label: name || `Resource #${r.id}` };
    });
  }, [resources]);

  const parentCustomerOptions = useMemo(() => {
    const idToSkip = isEdit ? Number(customer?.id) : null;
    return allCustomers
      .filter((c) => (idToSkip ? Number(c.id) !== idToSkip : true))
      .map((c) => ({ id: c.id, label: c.name || `Customer #${c.id}` }));
  }, [allCustomers, isEdit, customer?.id]);

  const contactOptions = useMemo(() => {
    return contacts.map((c) => ({
      id: c.id,
      label:
        [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
        c.email ||
        `Contact #${c.id}`,
    }));
  }, [contacts]);

  /* ===========================================
   * Render
   * =========================================*/
  return (
    <form
      ref={rootRef}
      onSubmit={(e) => handleSubmit(e)}
      className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-800 font-body w-full min-w-0"
      noValidate
    >
      {/* =================== CORE =================== */}
      <Field
        label="Company Name"
        name="name"
        value={form.name}
        onChange={handleChange}
        placeholder="Company Name"
        required
      />
      <Field
        label="Nickname"
        name="nickname"
        value={form.nickname || ""}
        onChange={handleChange}
        placeholder="Nickname"
      />

      <Field
        label="General Email"
        name="email"
        type="email"
        value={form.email || ""}
        onChange={handleChange}
        placeholder="e.g. info@company.com"
      />
      <Field
        label="Telephone"
        name="phone_number"
        value={form.phone_number || ""}
        onChange={handleChange}
        placeholder="Telephone Number"
      />

      <Field
        label="Website"
        name="website"
        type="url"
        value={form.website || ""}
        onChange={handleChange}
        placeholder="https://…"
      />
      <Field
        label="Company Number"
        name="company_number"
        value={form.company_number || ""}
        onChange={handleChange}
        placeholder="Company Number"
      />

      {/* Status / Category / Since / Internal Ref */}
      <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Select
          label="Status"
          name="status"
          value={form.status}
          onChange={handleChange}
          options={[
            { id: "Active", label: "Active" },
            { id: "Inactive", label: "Inactive" },
          ]}
          placeholder="Status…"
          required
        />
        <Select
          label="Category"
          name="category"
          value={form.category || ""}
          onChange={handleChange}
          options={CATEGORY_OPTIONS.map((c) => ({ id: c, label: c }))}
          placeholder="Category…"
        />
        <Field
          label="Customer Since"
          name="customer_since"
          type="date"
          value={form.customer_since || ""}
          onChange={handleChange}
          placeholder="yyyy-mm-dd"
        />
        <Field
          label="Internal Reference"
          name="internal_reference"
          value={form.internal_reference || ""}
          onChange={() => {}}
          placeholder={isEdit ? "—" : "Will be generated on save"}
          disabled
        />
      </div>

      <label className="col-span-1 md:col-span-2 flex min-h-[44px] items-center gap-3 rounded border p-3">
        <input
          type="checkbox"
          name="is_vendor"
          checked={form.is_vendor === true}
          onChange={handleChange}
          className="h-5 w-5"
        />
        <span>
          <strong>Is Vendor</strong>
          <span className="block text-sm opacity-75">
            Eligible for vendor price books and supplier selection. Does not change Lead / Customer status.
          </span>
        </span>
      </label>

      {/* =================== TRADING ADDRESS =================== */}
      <Field label="Address Line 1" name="address_line_1" value={form.address_line_1 || ""} onChange={handleChange} />
      <Field label="Address Line 2" name="address_line_2" value={form.address_line_2 || ""} onChange={handleChange} />
      <Field label="Address Line 3" name="address_line_3" value={form.address_line_3 || ""} onChange={handleChange} />
      <Field label="City" name="city" value={form.city || ""} onChange={handleChange} />
      <Field label="County / Province" name="county" value={form.county || ""} onChange={handleChange} />
      <div className="flex flex-col md:flex-row gap-4">
        <Field className="flex-1" label="Postcode / Zip" name="postcode" value={form.postcode || ""} onChange={handleChange} />
        <Field className="w-full md:w-48" label="Country" name="country" value={form.country || ""} onChange={handleChange} />
      </div>

      {/* =================== REGISTERED OFFICE =================== */}
      <Field label="Reg Address 1" name="reg_address_line_1" value={form.reg_address_line_1 || ""} onChange={handleChange} />
      <Field label="Reg Address 2" name="reg_address_line_2" value={form.reg_address_line_2 || ""} onChange={handleChange} />
      <Field label="Reg Address 3" name="reg_address_line_3" value={form.reg_address_line_3 || ""} onChange={handleChange} />
      <Field label="Reg City" name="reg_city" value={form.reg_city || ""} onChange={handleChange} />
      <Field label="Reg County" name="reg_county" value={form.reg_county || ""} onChange={handleChange} />
      <Field label="Reg Postcode" name="reg_postcode" value={form.reg_postcode || ""} onChange={handleChange} />

      {/* =================== BUSINESS INFO =================== */}
      <Field label="SIC Code" name="sic_code" value={form.sic_code || ""} onChange={handleChange} />
      <Field label="Formerly Known As" name="formerly_known_as" value={form.formerly_known_as || ""} onChange={handleChange} />
      <Field
        as="textarea"
        label="Nature of Business"
        name="nature_of_business"
        value={form.nature_of_business || ""}
        onChange={handleChange}
      />

      {/* =================== ASSOCIATIONS =================== */}
      <div className="col-span-1 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Account Manager (Resource)"
          name="account_manager_id"
          value={form.account_manager_id ?? ""}
          onChange={handleChange}
          options={resourceOptions}
          placeholder="Account Manager…"
        />
        <Select
          label="Closest Resource"
          name="closest_resource_id"
          value={form.closest_resource_id ?? ""}
          onChange={handleChange}
          options={resourceOptions}
          placeholder="Closest Resource…"
        />

        <Select
          label="Parent Company"
          name="parent_customer_id"
          value={form.parent_customer_id ?? ""}
          onChange={handleChange}
          options={parentCustomerOptions}
          placeholder="Parent Company…"
        />

        <Select
          label="Primary Contact"
          name="primary_contact_id"
          value={form.primary_contact_id ?? ""}
          onChange={handleChange}
          disabled={!isEdit || contactOptions.length === 0}
          options={contactOptions}
          placeholder="Primary Contact…"
        />

        <Select
          label="Billing Contact"
          name="billing_contact_id"
          value={form.billing_contact_id ?? ""}
          onChange={handleChange}
          disabled={!isEdit || contactOptions.length === 0}
          options={contactOptions}
          placeholder="Billing Contact…"
        />
      </div>

      {/* =================== NOTES =================== */}
      <div className="col-span-1 md:col-span-2">
        <Field
          as="textarea"
          label="Notes"
          name="notes"
          value={form.notes || ""}
          onChange={handleChange}
          className="min-h-[96px]"
        />
      </div>

      {/* =================== ACTIONS =================== */}
      <div className="col-span-1 md:col-span-2 flex flex-col md:flex-row md:justify-between gap-3 md:gap-4 mt-4">
        <button
          type="submit"
          disabled={submitting}
          className="w-full md:w-auto min-h-[44px] md:min-h-0 bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white font-body font-semibold py-2 px-4 rounded shadow transition-colors duration-200 ease-in-out"
          style={{ cursor: submitting ? "not-allowed" : "pointer" }}
          title="Save customer"
        >
          {submitting ? "Saving…" : isEdit ? "Update Customer" : "Save Customer"}
        </button>

        {!isEdit && showAddContactAction && (
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            className="w-full md:w-auto min-h-[44px] md:min-h-0 bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white font-body font-semibold py-2 px-4 rounded shadow transition-colors duration-200 ease-in-out"
            title="Save & add a contact"
          >
            Add Contact
          </button>
        )}
      </div>

      {/* =================== MESSAGES =================== */}
      {Object.keys(errors).length > 0 && (
        <div className="col-span-1 md:col-span-2 text-sm text-red-600 break-words">
          {Object.entries(errors).map(([k, v]) => (
            <div key={k}>• {v}</div>
          ))}
        </div>
      )}
      {message && <div className="col-span-1 md:col-span-2 text-sm text-gray-700 mt-2 break-words">{message}</div>}
    </form>
  );
};

export default CustomerFormWidget;
